'use client'

import * as React from 'react'
import { Card } from '@/components/ui/card'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

type CompanyCardVariant = 'transparent' | 'frosted' | 'ink'

interface CompanyCardProps {
    /** URL or path to the company logo image */
    logo: string
    /** Title text (e.g., "Technology") */
    title: string
    /** Body text (e.g., "Apple inc") */
    body: string
    /** Description text (e.g., "Phones, Headphones, software") */
    description: string
    /** Optional click handler for card interaction */
    onClick?: () => void
    /** Optional additional CSS classes */
    className?: string
    /** Visual style variant for the card */
    variant?: CompanyCardVariant
}

export function CompanyCard({
    logo,
    title,
    body,
    description,
    onClick,
    className,
    variant = 'transparent',
}: CompanyCardProps) {
    // Extract initials from body text for fallback (first 2 characters)
    const fallbackText = body
        .split(' ')
        .map((word) => word[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()

    const variantStyles: Record<CompanyCardVariant, string> = {
        transparent: 'bg-transparent',
        frosted: 'bg-white/50 dark:bg-white/50 backdrop-blur-[40px]',
        ink: 'bg-black/30 dark:bg-black/30 text-white',
    }

    return (
        <Card
            className={cn(
                'w-[342px] min-w-[342px] max-w-[342px] h-[85px] rounded-[30px] py-[8px] px-[10px] flex flex-row items-center gap-0 border-none shadow-none',
                variantStyles[variant],
                onClick &&
                    'cursor-pointer hover:bg-white/60 dark:hover:bg-white/60 transition-colors',
                className
            )}
            onClick={onClick}
        >
            {/* Logo Section */}
            <Avatar className="w-[65px] h-[65px] rounded-[20px] shrink-0">
                <AvatarImage src={logo} alt={body} className="object-contain" />
                <AvatarFallback className="rounded-[20px] bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60 text-sm font-medium">
                    {fallbackText}
                </AvatarFallback>
            </Avatar>

            {/* Text Content Section */}
            <div className="w-[250px] gap-[2px] flex flex-col py-[10px] px-[20px]">
                {/* Title */}
                <p className="text-[10px] leading-[12px] font-medium text-black/40 dark:text-white/40 truncate">
                    {title}
                </p>

                {/* Body */}
                <h3 className="text-[14px] leading-[20px] font-medium text-black/80 dark:text-white/80 truncate">
                    {body}
                </h3>

                {/* Description */}
                <p className="text-[12px] leading-[15px] font-normal text-black/40 dark:text-white/40 truncate">
                    {description}
                </p>
            </div>
        </Card>
    )
}
